import type { ReactElement } from 'react';
import type { ExtensionRiskEstimate } from '@kirin/api-client/contracts/extensions';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import {
  formatRiskLabel,
  NOT_ASSESSED_DESCRIPTION,
  NOT_ASSESSED_LABEL,
  NOT_SCANNED_BUILT_IN_DESCRIPTION,
  NOT_SCANNED_BUILT_IN_LABEL,
  riskEstimateLabel,
  riskLabelSeverity,
} from './agentmesh-risk-labels';

interface RiskEstimateCellProps {
  /** Optional on list rows until enrichment runs; null = not assessed. */
  riskEstimate: ExtensionRiskEstimate | undefined;
  /** Built-ins are never scanned; without a result they read "Not scanned — built-in" (KIR-4726). */
  isBuiltIn?: boolean;
  /**
   * Explain a label-less state on hover and to screen readers. Off where the
   * explanation is already on screen (the expanded row).
   */
  describe?: boolean;
}

/**
 * "Not assessed" / "Not scanned — built-in": muted, ellipsized when the
 * column is narrow, with the full label and why on hover (KIR-4726).
 */
function LabelLessState({
  label,
  description,
  describe,
}: {
  label: string;
  description: string;
  describe: boolean;
}): ReactElement {
  return (
    <span
      className="inline-flex max-w-full min-w-0 items-center gap-1.5 text-muted-foreground"
      title={describe ? `${label}: ${description}` : label}
    >
      <span
        aria-hidden
        className="size-1.5 shrink-0 rounded-full bg-muted-foreground/50"
      />
      <span className="truncate">{label}</span>
      {describe ? <span className="sr-only">: {description}</span> : null}
    </span>
  );
}

/**
 * Risk Estimate cell for inventory extension rows (KIR-1928) — the overall
 * AgentMesh score, styled like the Alerts severity column. The label is the
 * raw AgentMesh vocabulary (level, falling back to status); colors are a
 * best-effort map of known strings, neutral for unknown vocabulary.
 */
export function RiskEstimateCell({
  riskEstimate,
  isBuiltIn = false,
  describe = true,
}: RiskEstimateCellProps): ReactElement {
  const label = riskEstimateLabel(riskEstimate);
  // A built-in is never sent to AgentMesh; a result it does have (scanned
  // before it was classified built-in) still shows (KIR-4726).
  if (label === null) {
    return (
      <LabelLessState
        label={isBuiltIn ? NOT_SCANNED_BUILT_IN_LABEL : NOT_ASSESSED_LABEL}
        description={
          isBuiltIn
            ? NOT_SCANNED_BUILT_IN_DESCRIPTION
            : NOT_ASSESSED_DESCRIPTION
        }
        describe={describe}
      />
    );
  }

  const formatted = formatRiskLabel(label);
  return (
    <span className="inline-block max-w-full truncate" title={formatted}>
      <SeverityBadge
        riskLevel={riskLabelSeverity(label)}
        label={formatted}
        showIcon={false}
      />
    </span>
  );
}

RiskEstimateCell.displayName = 'RiskEstimateCell';
